import React, { useEffect } from 'react';
import ComparisonGroup from 'utils/components/FiltersModal/comparison/ComparisonGroup';
import {
    Comparison,
    fromFilter,
} from 'utils/components/FiltersModal/comparison/FilterComparison';
import { computeComparisonGroupProps } from 'utils/components/FiltersModal/formProps';
import { ColumnDefinitionsDictionary } from '@instinet-analytics/semantic-ui-components';
import { ColumnFilterState } from 'widgets/store/grid/filters/FilterState';
import { capitalize } from 'lodash';
import { EditedColumnNames } from 'grid/columnNames/hooks';

interface FilterComparisonEditorProps<TColumnId extends string, TDataItem> {
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>;
    editedColumnNames?: EditedColumnNames;
    data: TDataItem[];
    staticData?: { [dimension: string]: string[] };
    pendingColumnFilters: ColumnFilterState<TColumnId>[];
    pendingComparison: Comparison | undefined;
    isPendingComparisonInvalid: boolean;
    editingColumn: TColumnId | undefined;
    onSetPendingComparison: (
        comparison: Comparison | undefined,
        commitOnSet?: boolean
    ) => void;
    onCommitEditingComparison: () => void;
    noun?: string;
}

export default function FilterComparisonEditor<
    TColumnId extends string,
    TDataItem,
>(props: FilterComparisonEditorProps<TColumnId, TDataItem>) {
    const {
        columnDefinitions,
        data,
        editedColumnNames,
        staticData,
        pendingColumnFilters,
        pendingComparison,
        isPendingComparisonInvalid,
        editingColumn,
        noun,
        onSetPendingComparison,
        onCommitEditingComparison,
    } = props;

    useEffect(
        () => onSetPendingComparison(comparisonFromColumnFilter(), false),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editingColumn]
    );

    const editingColumnPendingFilter = pendingColumnFilters.find(
        (c) => c.column === editingColumn
    );

    const comparisonGroupProps = computeComparisonGroupProps(
        columnDefinitions,
        editingColumn,
        pendingComparison,
        onSetPendingComparison,
        editedColumnNames
    );

    const onInputEnter = () => onCommitEditingComparison();
    const onClickUpdateOrAdd = onInputEnter;

    return (
        <>
            <ComparisonGroup
                {...comparisonGroupProps}
                data={data}
                staticData={staticData}
                onInputEnter={onInputEnter}
            />
            <button
                className="ui button text-link Button update-or-add-filter-button"
                onClick={onClickUpdateOrAdd}
                disabled={isPendingComparisonInvalid}
            >
                {editingColumnPendingFilter ? 'Update' : 'Add'}{' '}
                {capitalize(noun) ?? 'Filter'}
            </button>
        </>
    );

    function comparisonFromColumnFilter(): Comparison | undefined {
        return editingColumnPendingFilter
            ? fromFilter(editingColumnPendingFilter.filter)
            : undefined;
    }
}
